import React from "react";
import { useParams } from "react-router-dom";
import Container from "../../Components/General/Container";
import { CASE_STUDIES } from "./data";
import styles from "./CaseStudyDetail.module.css";

// ─── Icon renderer ────────────────────────────────────────────────────────────
function CardIcon({ icon: Icon, iconImage, iconAlt, iconFull }) {
  if (iconImage) {
    // `iconFull` assets already contain the white disc, so they fill the 64px
    // slot; the older glyph-only assets sit at 32px inside the CSS circle.
    return (
      <img
        src={iconImage}
        alt={iconAlt || ""}
        height={iconFull ? 64 : 32}
        width={iconFull ? 64 : undefined}
        style={{ objectFit: "contain" }}
      />
    );
  }
  if (Icon) return <Icon size={40} className={styles.cardIcon} />;
  return null;
}

// ─── Highlights ──────────────────────────────────────────────────────────────
function HighlightCard({ item, fullWidth }) {
  return (
    <article className={`${styles.highlightCard}${fullWidth ? ` ${styles.highlightCardFull}` : ""}`}>
      {/* Figma 11950:31519 groups the badge with the value at a 16px gap and
          leaves 10px between that group and the caption, so the two spacings
          cannot come from one flat column. */}
      <div className={styles.cardHead}>
        <div className={`${styles.iconWrap}${item.iconFull ? ` ${styles.iconWrapFull}` : ""}`}>
          <CardIcon {...item} />
        </div>
        {item.value && <p className={styles.highlightValue}>{item.value}</p>}
        {item.title && <h3 className={styles.highlightTitle}>{item.title}</h3>}
      </div>
      {item.description && (
        <p className={styles.highlightDescription}>
          {item.description.split("\n").map((line, index) => (
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </p>
      )}
    </article>
  );
}

function HighlightsSection({ items }) {
  if (!items?.length) return null;
  const top = items.slice(0, 3);
  const bottom = items.slice(3);
  const isOdd = items.length % 2 !== 0;
  const bottomClass = bottom.length === 3 ? styles.highlightRowTop : styles.highlightRowBottom;
  return (
    <div className={styles.highlights}>
      <div className={styles.highlightRowTop}>
        {top.map((item, i) => (
          <HighlightCard
            key={i}
            item={item}
            fullWidth={isOdd && bottom.length === 0 && i === top.length - 1}
          />
        ))}
      </div>
      {bottom.length > 0 && (
        <div className={bottomClass}>
          {bottom.map((item, i) => (
            <HighlightCard
              key={i}
              item={item}
              fullWidth={isOdd && i === bottom.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Text ────────────────────────────────────────────────────────────────────
function TextSection({ heading, content }) {
  if (!content) return null;
  return (
    <div className={styles.sections}>
      {heading && <h2>{heading}</h2>}
      <div className={styles.sectionBody}>
        {content.split("\n\n").filter(Boolean).map((para, i) => (
          <p key={i} className={styles.sectionParagraph}>
            {para.trim().split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, pi) =>
              part.startsWith("**") && part.endsWith("**") ? (
                <strong key={pi}>{part.slice(2, -2)}</strong>
              ) : (
                <React.Fragment key={pi}>{part}</React.Fragment>
              )
            )}
          </p>
        ))}
      </div>
    </div>
  );
}

// ─── Lead-in lines ("Label: sentence") ───────────────────────────────────────
function PointLines({ points }) {
  if (!points?.length) return null;
  return (
    <div className={styles.points}>
      {points.map((p, i) => (
        <p key={i} className={styles.point}>
          <strong>{p.label}</strong> {p.text}
        </p>
      ))}
    </div>
  );
}

// ─── Spec chip strip ─────────────────────────────────────────────────────────
function SpecsSection({ items }) {
  if (!items?.length) return null;
  return (
    <div className={styles.specStrip}>
      {items.map((s, i) => (
        <div key={i} className={styles.specChip}>
          <span className={styles.specLabel}>{s.label}</span>
          <span className={styles.specValue}>{s.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Note callout ────────────────────────────────────────────────────────────
function NoteSection({ label = "NOTE", content }) {
  if (!content) return null;
  return (
    <aside className={styles.note}>
      <div className={styles.noteBody}>
        <p className={styles.noteLabel}>{label}</p>
        <p className={styles.noteText}>{content}</p>
      </div>
    </aside>
  );
}

// ─── Image above lead-in lines ───────────────────────────────────────────────
function ImageTextSection({ heading, image, imageAlt, points }) {
  return (
    <div className={styles.sections}>
      {heading && <h2>{heading}</h2>}
      {image && <img src={image} alt={imageAlt || ""} className={styles.wideImage} />}
      <PointLines points={points} />
    </div>
  );
}

// ─── Numbered mechanism grid ─────────────────────────────────────────────────
function MechanismSection({ heading, items }) {
  if (!items?.length) return null;
  return (
    <div className={styles.sections}>
      {heading && <h2>{heading}</h2>}
      <div className={styles.mechanismGrid}>
        {items.map((item, i) => (
          <article key={i} className={styles.mechanismItem}>
            <span className={styles.mechanismNumber}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

// ─── Image + parameter panel ─────────────────────────────────────────────────
function ParamPanelSection({ heading, points, image, imageAlt, panelTitle, params }) {
  return (
    <div className={styles.sections}>
      {heading && <h2>{heading}</h2>}
      <PointLines points={points} />
      <div className={styles.paramRow}>
        {image && <img src={image} alt={imageAlt || ""} className={styles.paramImage} />}
        <div className={styles.paramPanel}>
          {panelTitle && <h3 className={styles.paramPanelTitle}>{panelTitle}</h3>}
          <div className={styles.paramGrid}>
            {params?.map((p, i) => (
              <div key={i} className={styles.paramCell}>
                <span className={styles.paramLabel}>{p.label}</span>
                <span className={styles.paramValue}>{p.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Image gallery ───────────────────────────────────────────────────────────
function GallerySection({ heading, images }) {
  if (!images?.length) return null;
  return (
    <div className={styles.sections}>
      {heading && <h2>{heading}</h2>}
      <div className={styles.gallery}>
        {images.map((img, i) => (
          <img key={i} src={img.src} alt={img.alt || ""} className={styles.galleryImage} />
        ))}
      </div>
    </div>
  );
}

// ─── Table ───────────────────────────────────────────────────────────────────
function TableSection({ heading, headers, rows, variant }) {
  if (!headers?.length || !rows?.length) return null;
  return (
    <div className={styles.sections}>
      <h2>{heading}</h2>
      <div
        className={`${styles.tableWrap}${
          variant === "comparison" ? ` ${styles.tableComparison}` : ""
        }`}
      >
        <table>
          <thead>
            <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) =>
                  cell && typeof cell === "object" ? (
                    <td key={ci} colSpan={cell.colSpan || 1}>{cell.value}</td>
                  ) : (
                    <td key={ci}>{cell}</td>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Solutions ───────────────────────────────────────────────────────────────
function SolutionsSection({ items }) {
  if (!items?.length) return null;
  return (
    <div className={styles.sections}>
      <h2>Solutions Offered</h2>
      <div className={styles.solutions}>
        {items.map((s, i) => (
          <article key={i} className={styles.solutionCard}>
            {/* 24px badge-to-title gap, 16px below the group - Figma 11950:31767. */}
            <div className={styles.cardHead}>
              <div className={`${styles.iconWrap}${s.iconFull ? ` ${styles.iconWrapFull}` : ""}`}>
                <CardIcon {...s} />
              </div>
              <h3>{s.title.split("\n").map((line, lineIndex) => (
                <React.Fragment key={line}>
                  {lineIndex > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}</h3>
            </div>
            <p>{s.description}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

// ─── Cards (Impact / Problems) ───────────────────────────────────────────────
function CardsSection({ heading, intro, footer, items, columns = 3 }) {
  if (!items?.length) return null;
  return (
    <div className={styles.sections}>
      <h2>{heading}</h2>
      {intro && <p className={styles.sectionParagraph}>{intro}</p>}
      <div
        className={styles.infoCards}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {items.map((item, i) => (
          <article key={i} className={styles.infoCard}>
            {(item.iconImage || item.icon) && (
              <div className={`${styles.iconWrap}${item.iconFull ? ` ${styles.iconWrapFull}` : ""}`}>
                <CardIcon {...item} />
              </div>
            )}
            {item.title && <h3>{item.title}</h3>}
            <p>{item.description}</p>
          </article>
        ))}
      </div>
      {footer && <p className={styles.sectionParagraph}>{footer}</p>}
    </div>
  );
}

// ─── Metrics (Results) ───────────────────────────────────────────────────────
function MetricsSection({ heading, intro, items, columns = 4 }) {
  if (!items?.length) return null;
  return (
    <div className={styles.sections}>
      <h2>{heading}</h2>
      {intro && <p className={styles.sectionParagraph}>{intro}</p>}
      <div
        className={styles.metricGrid}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {items.map((item, i) => (
          <article key={i} className={styles.highlightCard}>
            <div className={styles.cardHead}>
              <div className={`${styles.iconWrap}${item.iconFull ? ` ${styles.iconWrapFull}` : ""}`}>
                <CardIcon {...item} />
              </div>
              {item.value && <p className={styles.highlightValue}>{item.value}</p>}
            </div>
            {item.description && <p className={styles.highlightDescription}>{item.description}</p>}
          </article>
        ))}
      </div>
    </div>
  );
}

// ─── Testimonial ─────────────────────────────────────────────────────────────
function TestimonialSection({ quote, author }) {
  if (!quote) return null;
  return (
    <div className={styles.sections}>
      <h2>Testimonial</h2>
      <div className={styles.testimonial}>
        <div className={styles.testimonialBody}>
          {quote.split("\n\n").filter(Boolean).map((para, i) => (
            <p key={i}>{para.trim()}</p>
          ))}
          {author && <cite>- {author}</cite>}
        </div>
      </div>
    </div>
  );
}

// ─── Renderer map ─────────────────────────────────────────────────────────────
const RENDERERS = {
  highlights:  (s, i) => <HighlightsSection key={i} {...s} />,
  text:        (s, i) => <TextSection       key={i} {...s} />,
  table:       (s, i) => <TableSection      key={i} {...s} />,
  solutions:   (s, i) => <SolutionsSection  key={i} {...s} />,
  cards:       (s, i) => <CardsSection      key={i} {...s} />,
  metrics:     (s, i) => <MetricsSection    key={i} {...s} />,
  testimonial: (s, i) => <TestimonialSection key={i} {...s} />,
  specs:       (s, i) => <SpecsSection      key={i} {...s} />,
  note:        (s, i) => <NoteSection       key={i} {...s} />,
  imageText:   (s, i) => <ImageTextSection  key={i} {...s} />,
  mechanism:   (s, i) => <MechanismSection  key={i} {...s} />,
  paramPanel:  (s, i) => <ParamPanelSection key={i} {...s} />,
  gallery:     (s, i) => <GallerySection    key={i} {...s} />,
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function CaseStudyDetail() {
  const { caseStudyId } = useParams();
  const cs = CASE_STUDIES[parseInt(caseStudyId, 10)] ?? null;

  if (!cs) {
    return (
      <div className={styles.emptyState}>
        <Container><p>Case study not found.</p></Container>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Container>
        <div className={styles.contentStack}>
          {/* div, not <header>: global element rules in style-responsive.css
              set a fixed height on bare header tags and clip the title */}
          <div className={styles.heroHeader}>
            <h1>{cs.title}</h1>
            {cs.client && <p className={styles.heroClient}>{cs.client}</p>}
            {cs.publishedOn && <p>Published on : {cs.publishedOn}</p>}
          </div>

          {/* Some studies (Figma 12050:3685) open straight into content — their
              `image` is the listing-card thumbnail only. */}
          {cs.image && !cs.hideHeroImage && (
            <img src={cs.image} alt={cs.title} className={styles.heroImage} />
          )}

          {cs.sections.map((section, i) => {
            const render = RENDERERS[section.type];
            return render ? render(section, i) : null;
          })}
        </div>
      </Container>
    </div>
  );
}